import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { BaseActor } from "./behaviors/BaseActor";
import Handlebars from "handlebars";

type Props = {
  actors: BaseActor[];
  camera: THREE.Camera;
  width: number;
  height: number;
};

export default function CalloutOverlay({ actors, camera, width, height }: Props) {

  const [callouts, setCallouts] = useState<
    {
      z: number;
      id: string;
      label: string;
      boxLeft: number;
      boxTop: number;
      modelProjectedX: number;
      modelProjectedY: number;
      isModelOnFarRight: boolean;
      arrowColor: string;
      actor: BaseActor;
      circleDistance: number; // Distance at which the circle appears
      boxWidth: number;
      boxHeight: number;
    }[]
  >([]);
  const contentRefs = useRef(new Map<string, HTMLDivElement>());

  // Handle callout updates
  useEffect(() => {
    const fixedActors = actors.map((actor, index) => ({
      ...actor,
      id: `${actor.id || 'actor'}-${index}`,
    }));

    const ids = fixedActors.map(a => a.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      console.warn('Duplicate actor IDs found:', ids.filter((id, index) => ids.indexOf(id) !== index));
    }

    let animationFrameId: number;

    const animation = () => {
      const updated = actors
        .filter((actor) => actor.calloutTemplate && !actor.suppressCallout && actor.obj && actor.obj.visible)
        .map((actor, index) => {
          const obj = actor.obj!;
          if (!obj) return null;

          const camPos = camera.position;
          const boundingBox = obj.getObjectByName("boundingBox");
          if (!boundingBox) return null;

          const box = new THREE.Box3().setFromObject(boundingBox);
          const center = new THREE.Vector3(
            (box.min.x + box.max.x) / 2,
            (box.min.y + box.max.y) / 2,
            (box.min.z + box.max.z) / 2
          );
          let label = '';

          try {
            if (!actor.calloutMetadata || !actor.calloutMetadata.renderer) {
              const renderer = Handlebars.compile(actor.calloutTemplate);
              actor.calloutMetadata = {
                renderer,
                arrowColor: 'red',
                maxDistance: 25,
                calloutHeight: 1.0,
                circleDistance: 2,
                calloutOffset: new THREE.Vector3(0, 0, 1.2),
              };

              if (actor.calloutTemplate!.startsWith('<!--')) {
                const endIndex = actor.calloutTemplate?.indexOf('-->');
                const metadataComment = actor.calloutTemplate?.substring(4, endIndex);
                const metadata = JSON.parse(metadataComment!);
                actor.calloutMetadata = { ...actor.calloutMetadata, ...metadata };
              }
            }

            label = actor.calloutMetadata!.renderer(actor, {
              allowProtoPropertiesByDefault: true,
              allowProtoMethodsByDefault: true,
              allowedProtoProperties: {
                __proto__: true // Explicitly allow if needed for your use case
              },
              allowedProtoMethods: {
                constructor: true,
                __defineGetter__: true,
                __defineSetter__: true,
                __lookupGetter__: true,
                __lookupSetter__: true
              }
            });
          } catch (e) {
            label = `Error: ${e}`;
            actor.calloutMetadata = {
              renderer: null,
              arrowColor: 'red',
              maxDistance: 25,
              calloutHeight: 1.9,
              circleDistance: 2,
              // calloutOffset: new THREE.Vector3(0, 0, 1.2),
            };
          }

          center.z += actor.calloutMetadata!.calloutHeight;

          const toCenter = center.clone().sub(camPos);
          const cameraDir = new THREE.Vector3();
          camera.getWorldDirection(cameraDir);

          if (toCenter.dot(cameraDir) < 0) return null;

          const distance = center.distanceTo(camPos);
          if (distance > actor.calloutMetadata!.maxDistance) return null;

          const projected = center.clone().project(camera);
          if (
            projected.z < 0 ||
            projected.x < -1 || projected.x > 1 ||
            projected.y < -1 || projected.y > 1
          ) return null;

          const modelProjectedX = (projected.x * 0.5 + 0.5) * width;
          const modelProjectedY = (-projected.y * 0.5 + 0.5) * height;

          const padding = 10;

          // Temporarily render the content to measure its dimensions
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.visibility = "hidden";
          tempDiv.style.maxWidth = "200px";
          tempDiv.style.padding = "6px 10px";
          tempDiv.style.fontSize = "12px";
          tempDiv.style.fontFamily = "sans-serif";
          tempDiv.style.wordWrap = "break-word";
          tempDiv.style.overflowWrap = "break-word";
          tempDiv.style.boxSizing = "border-box";
          tempDiv.style.color = "white";
          tempDiv.innerHTML = label;
          document.body.appendChild(tempDiv);
          const boxWidth = Math.min(tempDiv.offsetWidth, 200);
          const boxHeight = tempDiv.offsetHeight;
          document.body.removeChild(tempDiv);

          // Determine if the model is on the far right side
          const isModelOnFarRight = modelProjectedX > width * 0.95;

          // Position the box based on the model's position
          const verticalOffset = 150;
          let boxLeft: number;
          if (isModelOnFarRight) {
            boxLeft = Math.max(padding, modelProjectedX - boxWidth - 50);
          } else {
            boxLeft = modelProjectedX + 50;
          }
          let boxTop = modelProjectedY - verticalOffset - boxHeight;

          // Clamp the box position to prevent it from going offscreen
          boxLeft = Math.min(Math.max(boxLeft, padding), width - boxWidth - padding);
          boxTop = Math.min(Math.max(boxTop, padding), height - verticalOffset - boxHeight - padding);

          return {
            id: `${actor.id}-${index}`,
            label,
            boxLeft,
            boxTop,
            modelProjectedX,
            modelProjectedY,
            isModelOnFarRight,
            arrowColor: actor.calloutMetadata?.arrowColor || "rgba(0, 0, 0, 0.25)",
            actor,
            circleDistance: actor.calloutMetadata?.circleDistance || 150,
            boxWidth,
            boxHeight,
          };
        })
        .filter(Boolean) as any[];

      setCallouts(updated);
      animationFrameId = requestAnimationFrame(animation);
    };

    animation();

    return () => cancelAnimationFrame(animationFrameId);
  }, [actors, camera, width, height]);

  // Observe DOM resizing for dynamic content maybe we dont need this
  useEffect(() => {
    const observerMap = new Map<string, ResizeObserver>();

    callouts.forEach((c) => {
      const el = contentRefs.current.get(c.id);
      if (el && !observerMap.has(c.id)) {
        const observer = new ResizeObserver(() => {
          setCallouts((prev) => [...prev]);
        });
        observer.observe(el);
        observerMap.set(c.id, observer);
      }
    });

    return () => {
      observerMap.forEach((observer) => observer.disconnect());
    };
  }, [callouts]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width,
        height,
        pointerEvents: "none",
      }}
    >
      {callouts.map((c) => {
        const contentEl = contentRefs.current.get(c.id);
        const boxWidth = contentEl?.offsetWidth || c.boxWidth;
        const boxHeight = contentEl?.offsetHeight || c.boxHeight;

        // Line starts from the bottom-right corner of the box when model is on far right
        const lineStartX = c.isModelOnFarRight ? c.boxLeft + boxWidth + 10 : c.boxLeft + 11.5;
        const lineStartY = c.boxTop + boxHeight + 4;

        let lineEndX = c.modelProjectedX;
        let lineEndY = c.modelProjectedY;

        const boundingBoxObj = c.actor.obj?.getObjectByName("boundingBox");
        let circleDiameter = 0, circleLeft = 0, circleTop = 0, circleCenterX = 0, circleCenterY = 0;
        let distance = 0; // Initialize distance here for broader scope

        if (boundingBoxObj) {
          const box = new THREE.Box3().setFromObject(boundingBoxObj);
          const centerOfBoxWorld = new THREE.Vector3(
            (box.min.x + box.max.x) / 2,
            (box.min.y + box.max.y) / 2,
            (box.min.z + box.max.z) / 2
          );

          distance = centerOfBoxWorld.distanceTo(camera.position);

          const projected = centerOfBoxWorld.clone().project(camera);
          lineEndX = (projected.x * 0.5 + 0.5) * width;
          lineEndY = (-projected.y * 0.5 + 0.5) * height;

          const corners = [
            new THREE.Vector3(box.min.x, box.min.y, box.min.z),
            new THREE.Vector3(box.max.x, box.max.y, box.max.z),
          ];

          const projectedCorners = corners.map(corner => {
            const projected = corner.clone().project(camera);
            return {
              x: (projected.x * 0.5 + 0.5) * width,
              y: (-projected.y * 0.5 + 0.5) * height,
            };
          });

          // Calculate the projected size of the bounding box
          const projectedWidth = Math.abs(projectedCorners[1].x - projectedCorners[0].x);
          const projectedHeight = Math.abs(projectedCorners[1].y - projectedCorners[0].y);
          const minCircleDiameter = Math.max(projectedWidth, projectedHeight); // Minimum diameter based on bounding box projection

          // Calculate the base circle diameter with conditional padding
          let baseDiameter = minCircleDiameter;
          if (c.circleDistance !== undefined) {
            const circlePadding = 15; // Add 50px padding when circleDistance is defined
            baseDiameter += circlePadding;
          }

          // Scale up when super close
          const closeDistanceThreshold = 1;
          const fixedDiameter = 200; // Fixed diameter when super close
          circleDiameter = distance < closeDistanceThreshold ? fixedDiameter : baseDiameter;

          // Ensure circle diameter is never smaller than the minimum with padding | if padding changes, adjust here too 
          circleDiameter = Math.max(circleDiameter, minCircleDiameter + (c.circleDistance !== undefined ? 15 : 0));

          circleCenterX = (projectedCorners[0].x + projectedCorners[1].x) / 2;
          circleCenterY = (projectedCorners[0].y + projectedCorners[1].y) / 2;
          circleLeft = circleCenterX - circleDiameter / 2;
          circleTop = circleCenterY - circleDiameter / 2;

          // Adjust the line's end point to intersect the circle only if circleDistance is defined and within distance
          if (c.circleDistance !== undefined && distance >= c.circleDistance && c.isModelOnFarRight) {
            const dxToCenter = circleCenterX - lineStartX;
            const dyToCenter = circleCenterY - lineStartY;
            const distanceToCenter = Math.sqrt(dxToCenter * dxToCenter + dyToCenter * dyToCenter);

            const radius = circleDiameter / 2;
            const normalizedDx = dxToCenter / distanceToCenter;
            const normalizedDy = dyToCenter / distanceToCenter;

            lineEndX = circleCenterX - normalizedDx * radius;
            lineEndY = circleCenterY - normalizedDy * radius;
          }
        }

        const dx = lineEndX - lineStartX;
        const dy = lineEndY - lineStartY;
        const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
        const length = Math.sqrt(dx * dx + dy * dy);

        // Calculate the line length, so it touches the model when no circle is shown
        let lineVisibleLength = length;
        if (c.circleDistance !== undefined && distance >= c.circleDistance) {
          const lineLengthToCircleEdge = Math.min(
            Math.sqrt((circleCenterX - lineStartX) ** 2 + (circleCenterY - lineStartY) ** 2) - circleDiameter / 2,
            length
          );
          lineVisibleLength = Math.max(0, lineLengthToCircleEdge);
        }

        return (
          <div key={c.id}>
            {/* The box */}
            <div
              style={{
                position: "absolute",
                transform: "none",
                left: `${c.boxLeft}px`,
                top: `${c.boxTop}px`,
                maxWidth: "200px",
                color: "white",
                padding: "6px 10px",
                fontSize: "12px",
                fontFamily: "sans-serif",
                pointerEvents: "none",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              <div
                ref={(el) => {
                  if (el) contentRefs.current.set(c.id, el);
                }}
                style={{ display: "inline-block" }}
              >
                <div dangerouslySetInnerHTML={{ __html: c.label }} />
              </div>
              {/* The line */}
              <div
                style={{
                  position: "absolute",
                  left: `${lineStartX - c.boxLeft}px`,
                  top: `${lineStartY - c.boxTop}px`,
                  width: `${lineVisibleLength}px`,
                  height: "2px",
                  backgroundColor: c.arrowColor,
                  transform: `rotate(${angleDeg}deg)`,
                  transformOrigin: "0 0",
                  pointerEvents: "none",
                }}
              />
            </div>

            {c.circleDistance && boundingBoxObj && (() => {

              if (distance >= c.circleDistance) {
                return (
                  <div
                    style={{
                      position: "absolute",
                      left: `${circleLeft}px`,
                      top: `${circleTop}px`,
                      width: `${circleDiameter}px`,
                      height: `${circleDiameter}px`,
                      borderRadius: "50%",
                      border: `2px solid ${c.arrowColor}`,
                      pointerEvents: "none",
                    }}
                  />
                );
              }

              return null;
            })()}
          </div>
        );
      })}
    </div>
  );
}